import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../utils/api';
import {
  MapPin, Calendar, Award, Users, GraduationCap,
  Book, Check, FileText, Mail, Info, Home
} from 'lucide-react';

interface SchoolData {
  id: string;
  name: string;
  type: string;
  postcode: string;
  city: string;
  address_line1?: string;
  day_fees?: number;
  boarding_fees?: number;
  age_min?: number;
  age_max?: number;
  gender?: string;
  curriculum?: string;
  specialisms?: string;
  summary?: string;
  description?: string;
  rating?: number;
  availability?: string;
  verified?: number;
  openDays?: any[];
}

export default function SchoolProfile() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [school, setSchool] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadSchool();
  }, [id]);

  async function loadSchool() {
    try {
      const res = await api.getSchool(id!);
      setSchool(res.school);
    } catch (error) {
      console.error('Failed to load school:', error);
      showToast('Failed to load school profile', 'error');
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (amount?: number) => {
    return amount ? new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(amount) : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading school profile...</p>
        </div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">School Not Found</h2>
          <p className="text-gray-600 mb-4">The school profile you're looking for doesn't exist.</p>
          <button onClick={() => navigate(-1)} className="btn btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const specialismsArray = school.specialisms ? school.specialisms.split(',').map(s => s.trim()) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <GraduationCap className="h-8 w-8 text-primary-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-800">IndieSchools</h1>
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <Link
                  to={`/${user.role}`}
                  className="flex items-center text-gray-600 hover:text-primary-600"
                >
                  <Home className="h-5 w-5 mr-1" />
                  Dashboard
                </Link>
              )}
              {!user && (
                <Link to="/login" className="btn btn-primary">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <button onClick={() => navigate(-1)} className="hover:text-primary-600">
              ← Back
            </button>
            {' / '}
            <span className="text-gray-800">{school.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* School Header */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary-600 to-blue-800 p-6 text-white">
            <div className="flex flex-col md:flex-row justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">{school.name}</h1>
                {school.summary && (
                  <p className="text-blue-100 mb-4">{school.summary}</p>
                )}
                <div className="flex items-center text-blue-50 mb-2">
                  <MapPin className="h-4 w-4 mr-2" />
                  {school.city} • {school.postcode}
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {school.type && (
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      {school.type}
                    </span>
                  )}
                  {school.age_min && school.age_max && (
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      Ages {school.age_min}-{school.age_max}
                    </span>
                  )}
                  {school.gender && (
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      {school.gender}
                    </span>
                  )}
                  {school.curriculum && (
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      {school.curriculum}
                    </span>
                  )}
                  {school.verified === 1 && (
                    <span className="bg-green-500/30 px-3 py-1 rounded-full text-sm flex items-center">
                      <Check className="h-4 w-4 mr-1" />
                      Verified
                    </span>
                  )}
                </div>
              </div>

              {school.rating && school.rating > 0 && (
                <div className="mt-6 md:mt-0 md:text-right">
                  <div className="inline-block bg-green-500 text-white px-4 py-2 rounded-lg mb-2">
                    <div className="flex items-center">
                      <Award className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Rating: {school.rating.toFixed(1)}/5</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-wrap gap-6 justify-between mb-6">
              {school.day_fees && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Day Fees</h3>
                  <div className="font-semibold text-gray-800 text-2xl">{formatCurrency(school.day_fees)}</div>
                  <div className="text-sm text-gray-500">per annum</div>
                </div>
              )}

              {school.boarding_fees && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Boarding Fees</h3>
                  <div className="font-semibold text-gray-800 text-2xl">{formatCurrency(school.boarding_fees)}</div>
                  <div className="text-sm text-gray-500">per annum</div>
                </div>
              )}

              {school.availability && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Availability</h3>
                  <div className="text-lg">
                    <span className={`badge ${
                      school.availability === 'available' ? 'badge-success' :
                      school.availability === 'limited' ? 'badge-warning' : 'badge-danger'
                    }`}>
                      {school.availability.charAt(0).toUpperCase() + school.availability.slice(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Contact through IndieSchools */}
            {user?.role === 'parent' && (
              <div className="flex justify-center border-t pt-6">
                <button
                  onClick={() => navigate('/parent')}
                  className="btn btn-primary py-3 px-8"
                >
                  <Mail className="h-5 w-5 mr-2 inline" />
                  Contact School via IndieSchools
                </button>
              </div>
            )}
            {!user && (
              <div className="flex justify-center border-t pt-6">
                <Link to="/register?role=parent" className="btn btn-primary py-3 px-8">
                  <Mail className="h-5 w-5 mr-2 inline" />
                  Sign Up to Contact School
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Tabs and Content */}
        <div className="grid grid-cols-1 gap-8">
          <div>
            {/* Tab Navigation */}
            <div className="bg-white rounded-t-lg border border-b-0 mb-0">
              <div className="flex overflow-x-auto">
                {['overview', 'admissions', 'curriculum', 'facilities', 'reviews'].map((tab) => (
                  <button
                    key={tab}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                      activeTab === tab
                        ? 'text-primary-600 border-b-2 border-primary-600'
                        : 'text-gray-600 hover:text-primary-600'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-b-lg border p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">School Overview</h2>
                  {school.description ? (
                    <p className="text-gray-600 mb-6 whitespace-pre-line">{school.description}</p>
                  ) : (
                    <p className="text-gray-500 mb-6 italic">
                      Detailed information about {school.name} will be available soon.
                    </p>
                  )}

                  {(() => {
                    try {
                      const highlights = school.inspection_highlights ? JSON.parse(school.inspection_highlights) : [];
                      if (highlights.length > 0) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <Award className="h-5 w-5 text-primary-600 mr-2" />
                              Inspection Highlights
                            </h3>
                            <div className="space-y-2">
                              {highlights.map((highlight: string, index: number) => (
                                <div key={index} className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">{highlight}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {(() => {
                    try {
                      const achievements = school.academic_achievements ? JSON.parse(school.academic_achievements) : {};
                      const hasData = achievements.gcse_percent || achievements.alevel_percent || achievements.oxbridge_percent || achievements.teacher_pupil_ratio;
                      if (hasData) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <Award className="h-5 w-5 text-primary-600 mr-2" />
                              Academic Achievements
                            </h3>
                            <div className="space-y-2">
                              {achievements.gcse_percent && (
                                <div className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">GCSE: {achievements.gcse_percent} grades 9-7 (A*-A)</span>
                                </div>
                              )}
                              {achievements.alevel_percent && (
                                <div className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">A-Level: {achievements.alevel_percent} grades A*-B</span>
                                </div>
                              )}
                              {achievements.oxbridge_percent && (
                                <div className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">Oxbridge: {achievements.oxbridge_percent} of leavers</span>
                                </div>
                              )}
                              {achievements.teacher_pupil_ratio && (
                                <div className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">{achievements.teacher_pupil_ratio} teacher to pupil ratio</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {(() => {
                    try {
                      const extraCurricular = school.extra_curricular ? JSON.parse(school.extra_curricular) : {};
                      const hasData = extraCurricular.sport || extraCurricular.music || extraCurricular.clubs;
                      if (hasData) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <Award className="h-5 w-5 text-primary-600 mr-2" />
                              Extra-Curricular Activities
                            </h3>
                            {extraCurricular.sport && (
                              <div className="mb-3">
                                <p className="font-medium text-gray-800 mb-1">Sport:</p>
                                <p className="text-gray-700">{extraCurricular.sport}</p>
                              </div>
                            )}
                            {extraCurricular.music && (
                              <div className="mb-3">
                                <p className="font-medium text-gray-800 mb-1">Music:</p>
                                <p className="text-gray-700">{extraCurricular.music}</p>
                              </div>
                            )}
                            {extraCurricular.clubs && (
                              <div className="mb-3">
                                <p className="font-medium text-gray-800 mb-1">Clubs:</p>
                                <p className="text-gray-700">{extraCurricular.clubs}</p>
                              </div>
                            )}
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {specialismsArray.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <Award className="h-5 w-5 text-primary-600 mr-2" />
                        Specialisms
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {specialismsArray.map((specialism, index) => (
                          <span key={index} className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm">
                            {specialism}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Admissions Tab */}
              {activeTab === 'admissions' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Admissions</h2>

                  {school.age_min && school.age_max && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Age Range</h3>
                      <p className="text-gray-700">
                        {school.name} accepts students from ages {school.age_min} to {school.age_max}.
                      </p>
                    </div>
                  )}

                  {(() => {
                    try {
                      const entryPoints = school.main_entry_points ? JSON.parse(school.main_entry_points) : [];
                      if (entryPoints.length > 0) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Main Entry Points</h3>
                            <div className="flex flex-wrap gap-2">
                              {entryPoints.map((age: number) => (
                                <span key={age} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">
                                  Age {age}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {school.application_deadline && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Application Deadline</h3>
                      <p className="text-gray-700">{school.application_deadline}</p>
                    </div>
                  )}

                  {school.entry_requirements && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Entry Requirements</h3>
                      <p className="text-gray-700 whitespace-pre-line">{school.entry_requirements}</p>
                    </div>
                  )}

                  {school.application_process && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Application Process</h3>
                      <p className="text-gray-700 whitespace-pre-line">{school.application_process}</p>
                    </div>
                  )}

                  {school.scholarships && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Scholarships & Bursaries</h3>
                      <p className="text-gray-700 whitespace-pre-line">{school.scholarships}</p>
                    </div>
                  )}

                  {school.registration_fees && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Registration Fees</h3>
                      <p className="text-gray-700 whitespace-pre-line">{school.registration_fees}</p>
                    </div>
                  )}

                  {school.tours_info && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">School Tours & Visits</h3>
                      <p className="text-gray-700 whitespace-pre-line">{school.tours_info}</p>
                    </div>
                  )}

                  {school.openDays && school.openDays.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <Calendar className="h-5 w-5 text-primary-600 mr-2" />
                        Upcoming Open Days
                      </h3>
                      <div className="space-y-3">
                        {school.openDays.map((day, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                            <div>
                              <div className="font-medium text-gray-800">{new Date(day.date).toLocaleDateString()}</div>
                              {day.time_start && day.time_end && (
                                <div className="text-sm text-gray-600">{day.time_start} - {day.time_end}</div>
                              )}
                              {day.notes && (
                                <div className="text-sm text-gray-600 mt-1">{day.notes}</div>
                              )}
                            </div>
                            {day.capacity && day.booked !== undefined && (
                              <div className="text-sm text-gray-600">
                                {day.capacity - day.booked} spots available
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-900">
                          <strong>Note:</strong> All enquiries and communications with schools are handled through
                          the IndieSchools platform to ensure security and provide you with the best experience.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Curriculum Tab */}
              {activeTab === 'curriculum' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Curriculum</h2>

                  {school.curriculum && (
                    <div className="mb-6">
                      <p className="text-gray-700">
                        Broad and challenging curriculum focusing on academic excellence while developing critical thinking and independent learning skills.
                      </p>
                    </div>
                  )}

                  {(() => {
                    try {
                      const subjects = school.subject_offerings ? JSON.parse(school.subject_offerings) : [];
                      if (subjects.length > 0) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <Book className="h-5 w-5 text-primary-600 mr-2" />
                              Subject Offerings
                            </h3>
                            <div className="space-y-2">
                              {subjects.map((subject: string, index: number) => (
                                <div key={index} className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span className="text-gray-700">{subject}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {school.curriculum && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <Book className="h-5 w-5 text-primary-600 mr-2" />
                        Curriculum Type
                      </h3>
                      <p className="text-gray-700">{school.curriculum}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Facilities Tab */}
              {activeTab === 'facilities' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Facilities</h2>
                  {(() => {
                    try {
                      const facilities = school.facilities_list ? JSON.parse(school.facilities_list) : [];
                      if (facilities.length > 0) {
                        return (
                          <div className="space-y-6">
                            {facilities.map((facility: {title: string, description: string}, index: number) => (
                              <div key={index} className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{facility.title}</h3>
                                <p className="text-gray-700">{facility.description}</p>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p className="text-gray-600">
                          For information about {school.name}'s facilities and campus, please contact the school
                          through our messaging system.
                        </p>
                      );
                    } catch {
                      return (
                        <p className="text-gray-600">
                          For information about {school.name}'s facilities and campus, please contact the school
                          through our messaging system.
                        </p>
                      );
                    }
                  })()}
                </div>
              )}

              {/* Reviews Tab */}
              {activeTab === 'reviews' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Reviews & Ratings</h2>

                  {school.inspection_rating && (
                    <div className="mb-6">
                      <div className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold mb-2">
                        {school.inspection_rating}
                      </div>
                      {school.inspection_date && (
                        <p className="text-gray-600 text-sm">Last inspected {school.inspection_date}</p>
                      )}
                    </div>
                  )}

                  {(() => {
                    try {
                      const highlights = school.inspection_highlights ? JSON.parse(school.inspection_highlights) : [];
                      if (highlights.length > 0) {
                        return (
                          <div className="space-y-2">
                            {highlights.map((highlight: string, index: number) => (
                              <div key={index} className="flex items-start">
                                <span className="text-green-600 mr-2">✓</span>
                                <span className="text-gray-700">{highlight}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}

                  {!school.inspection_rating && (
                    <p className="text-gray-600">
                      Inspection information for {school.name} will be available soon.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
